import {useEffect,useMemo,useRef,useState} from 'react'
import {AlertTriangle,Check,Clock,ClipboardCopy,ExternalLink} from 'lucide-react'
import {getContacts,getOpportunities,issueAttributedCheckout} from './lib/api'
import {
 beginCheckoutAttempt,
 canWalkToCheckout,
 completeCheckoutAttempt,
 createCheckoutAttempt,
 extractAttributionRef,
 failCheckoutAttempt,
 providerCapabilityState,
 redactForDisplay,
 resetCheckoutAttempt,
 secondsRemaining,
 sessionExpiryState,
 walkToCheckoutBlocker,
 type CanonicalCheckoutPlan,
 type CheckoutAttempt,
 type PaymentDeskSession,
} from './lib/paymentDesk'
import type {Contact,Opportunity} from './types'

const PLANS:readonly CanonicalCheckoutPlan[]=['watch','protect','shield']
const PLAN_LABEL:Record<CanonicalCheckoutPlan,string>={watch:'Watch · $79/month',protect:'Protect · $149/month',shield:'Shield · $299/month'}
const BLOCKER_COPY:Record<string,string>={
 opportunity_required:'Select an open opportunity to walk the customer to checkout.',
 opportunity_stage_closed:'Attributed checkout requires an open opportunity stage (discovery, qualified, proposal, or decision).',
 contact_email_unreachable:'The primary contact needs a reachable email address before a checkout link can be issued.',
}
const PROVIDER_PENDING_BANNER='Provider pending — payment capture is not available in this desk yet. Verified payments arrive from the provider webhook.'

function formatCountdown(totalSeconds:number){return `${Math.floor(totalSeconds/60)}:${String(totalSeconds%60).padStart(2,'0')}`}

export function PaymentDesk(){
 const[opportunities,setOpportunities]=useState<Opportunity[]>([])
 const[contacts,setContacts]=useState<Contact[]>([])
 const[loadStatus,setLoadStatus]=useState<'loading'|'ready'|'error'>('loading')
 const[selectedOpportunityId,setSelectedOpportunityId]=useState('')
 const[planId,setPlanId]=useState<CanonicalCheckoutPlan>('protect')
 const[locationCountValue,setLocationCountValue]=useState('1')
 const[session,setSession]=useState<PaymentDeskSession|null>(null)
 const[issuedUrl,setIssuedUrl]=useState<string|null>(null)
 const[issuing,setIssuing]=useState(false)
 const[issueError,setIssueError]=useState<string|null>(null)
 const[copied,setCopied]=useState(false)
 const[copyError,setCopyError]=useState(false)
 const[now,setNow]=useState(()=>Date.now())
 const takePaymentState=providerCapabilityState()
 const attemptRef=useRef<CheckoutAttempt>(createCheckoutAttempt(crypto.randomUUID()))

 useEffect(()=>{let active=true;Promise.allSettled([getOpportunities(),getContacts()]).then(([opportunitiesResult,contactsResult])=>{if(!active)return;setOpportunities(opportunitiesResult.status==='fulfilled'?opportunitiesResult.value:[]);setContacts(contactsResult.status==='fulfilled'?contactsResult.value:[]);setLoadStatus(opportunitiesResult.status==='fulfilled'&&contactsResult.status==='fulfilled'?'ready':'error')});return()=>{active=false}},[])
 useEffect(()=>{if(!session)return;const timer=setInterval(()=>setNow(Date.now()),1000);return()=>clearInterval(timer)},[session])

 const selectedOpportunity=useMemo(()=>opportunities.find(item=>item.id===selectedOpportunityId)||null,[opportunities,selectedOpportunityId])
 const primaryContact=useMemo(()=>selectedOpportunity?(contacts.find(item=>item.id===selectedOpportunity.primaryContactId)||null):null,[contacts,selectedOpportunity])
 const contactEmail=primaryContact?.email?.trim()||null
 const walkable=canWalkToCheckout(selectedOpportunity,contactEmail)
 const blocker=walkToCheckoutBlocker(selectedOpportunity,contactEmail)
 const locationCount=Number(locationCountValue)
 const inputValid=Number.isInteger(locationCount)&&locationCount>=1&&locationCount<=10000
 const expiryState=session?sessionExpiryState(session.expiresAt,now):null
 const remainingSeconds=session?secondsRemaining(session.expiresAt,now):0
 const maskedReference=useMemo(()=>session?redactForDisplay(session).attributionRef:null,[session])

 async function handleIssue(){
  if(!selectedOpportunity||!primaryContact||!contactEmail||!walkable||!inputValid||issuing||attemptRef.current.status==='ISSUING')return
  setIssuing(true);setIssueError(null);setCopied(false);setCopyError(false)
  attemptRef.current=beginCheckoutAttempt(attemptRef.current)
  try{
   const result=await issueAttributedCheckout({organizationId:selectedOpportunity.organizationId,contactId:primaryContact.id,opportunityId:selectedOpportunity.id,planId,locationCount,idempotencyKey:attemptRef.current.idempotencyKey})
   const attributionRef=extractAttributionRef(result.checkoutUrl)
   if(!attributionRef)throw new Error('invalid_checkout_reference')
   attemptRef.current=completeCheckoutAttempt(attemptRef.current)
   setNow(Date.now())
   setIssuedUrl(result.checkoutUrl)
   setSession({attributionRef,expiresAt:result.expiresAt,canonicalPlanId:planId,providerState:takePaymentState})
  }catch(error){
   attemptRef.current=failCheckoutAttempt(attemptRef.current)
   setIssueError(error instanceof Error&&error.message?error.message.replaceAll('_',' '):'The checkout link could not be issued. Retry with the same secure request.')
  }finally{setIssuing(false)}
 }

 async function handleCopy(){
  if(!issuedUrl)return
  try{await navigator.clipboard.writeText(issuedUrl);setCopied(true);setCopyError(false)}catch{setCopied(false);setCopyError(true)}
 }

 function startNextCheckout(){
  attemptRef.current=resetCheckoutAttempt(crypto.randomUUID())
  setSession(null);setIssuedUrl(null);setIssueError(null);setCopied(false);setCopyError(false);setSelectedOpportunityId('')
 }

 return <section className="page-shell"><div className="page-heading"><div><span className="eyebrow">ATTRIBUTED CHECKOUT</span><h1>Payment Desk</h1><p>Walk a customer to self-service checkout with an opaque attributed link. The link never exposes rep identity, and checkout initiation never creates a customer record.</p></div></div>
  {loadStatus==='loading'&&<div className="panel"><p>Loading opportunities and contacts…</p></div>}
  {loadStatus==='error'&&<div className="panel"><p role="alert">Opportunities and contacts are unavailable right now. No checkout links can be issued until canonical CRM records load.</p></div>}
  {loadStatus==='ready'&&<div className="panel"><div className="panel-header"><div><h3>Walk customer to checkout</h3><p>Pick an open opportunity whose primary contact has an email. The desk issues one opaque link per idempotent attempt.</p></div></div>
   <div className="toolbar-row">
    <select aria-label="Select opportunity" value={selectedOpportunityId} onChange={event=>{setSelectedOpportunityId(event.target.value);setSession(null);setIssuedUrl(null);setIssueError(null)}}>
     <option value="">Select opportunity…</option>
     {opportunities.map(item=><option key={item.id} value={item.id}>{item.name} · {item.organizationName} · {item.stage}</option>)}
    </select>
    <select aria-label="Canonical plan" value={planId} onChange={event=>setPlanId(event.target.value as CanonicalCheckoutPlan)}>
     {PLANS.map(value=><option key={value} value={value}>{PLAN_LABEL[value]}</option>)}
    </select>
    <input aria-label="Location count" type="number" min={1} max={10000} value={locationCountValue} onChange={event=>setLocationCountValue(event.target.value)}/>
    <button className="primary-btn" type="button" disabled={!walkable||!inputValid||issuing} onClick={()=>void handleIssue()}>{issuing?'Issuing…':'Issue secure checkout link'}</button>
   </div>
   {!walkable&&<p role="note">{BLOCKER_COPY[blocker??'opportunity_required']}</p>}
   {blocker===null&&!inputValid&&<p role="note">Location count must be a whole number between 1 and 10000.</p>}
   {primaryContact&&<p>Contact: {primaryContact.name}{contactEmail?` · ${contactEmail}`:' · no reachable email'}</p>}
   {issueError&&<p role="alert"><AlertTriangle size={14}/> {issueError}{attemptRef.current.status==='FAILED'?' The same idempotency key will be reused on retry.':''}</p>}
  </div>}
  {issuedUrl&&session&&<div className="panel"><div className="panel-header"><div><h3>Send secure payment link</h3><p>Share this link with the customer over a channel you control. It expires 30 minutes after issuance.</p></div></div>
   <div className="toolbar-row">
    <input aria-label="Attributed checkout URL" readOnly value={issuedUrl} onFocus={event=>event.currentTarget.select()}/>
    <button className="secondary-btn" type="button" onClick={()=>void handleCopy()}>{copied?<><Check size={14}/> Copied</>:<><ClipboardCopy size={14}/> Copy link</>}</button>
    <a className="secondary-btn" href={issuedUrl} target="_blank" rel="noreferrer noopener"><ExternalLink size={14}/> Open owner checkout</a>
   </div>
   {copyError&&<p role="alert">Clipboard access was blocked. Select the link text and copy it manually.</p>}
   <p>Reference {maskedReference} · Plan {session.canonicalPlanId} · Provider state {session.providerState.replaceAll('_',' ')}</p>
   {expiryState==='ACTIVE'&&<p role="status"><Clock size={14}/> Expires in {formatCountdown(remainingSeconds)}</p>}
   {expiryState==='EXPIRED'&&<p role="alert"><strong>Expired.</strong> This checkout window closed. Issue a new link to restart the walk.</p>}
   <button className="secondary-btn" type="button" onClick={startNextCheckout}>Start next checkout</button>
  </div>}
  <div className="panel"><div className="panel-header"><div><h3>Take payment</h3><p>This desk reflects provider capability truthfully.</p></div></div>
   {takePaymentState==='PROVIDER_PENDING'&&<div role="note" style={{borderLeft:'3px solid var(--line-strong)',padding:'8px 11px',background:'var(--panel-2)',borderRadius:8}}><strong>PROVIDER PENDING</strong><p>{PROVIDER_PENDING_BANNER}</p><span className="status-chip">NOT HEALTHY — AWAITING PROVIDER</span></div>}
   {takePaymentState==='AVAILABLE'&&<div role="status"><strong>AVAILABLE</strong><p>A provider take-payment capability is explicitly configured. Collected amounts become commission-relevant only after the provider webhook delivers verified revenue.</p></div>}
  </div>
 </section>
}
